import React, { useRef, useEffect, useState } from 'react';
import FallbackImage from './FallbackImage';
import Header from './Header';
import { CALENDLY_URL, SITE_URL } from '../config';
import styles from './LandingPage.module.css';

function FadeInSection({ children }) {
  const domRef = useRef();
  const [isVisible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setVisible(true); }); },
      { threshold: 0.08 }
    );
    if (domRef.current) observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div className={`fade-in-section${isVisible ? ' is-visible' : ''}`} ref={domRef}>
      {children}
    </div>
  );
}

function FadeInCard({ children, delay = 0 }) {
  const domRef = useRef();
  const [isVisible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setVisible(true); }); },
      { threshold: 0.1 }
    );
    if (domRef.current) observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      className={`fade-in-card${isVisible ? ' is-visible' : ''}`}
      ref={domRef}
      style={{ '--stagger-delay': `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function SectionHeading({ id, children }) {
  return (
    <div id={id} className={styles.sectionHeadingWrap}>
      <span className={styles.sectionHeadingBadge} aria-hidden="true" />
      <h3 className={styles.sectionHeading}>{children}</h3>
      <div className={styles.sectionHeadingLine} aria-hidden="true" />
    </div>
  );
}

const serviceIcons = [
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M4 20c0-4.418 3.582-8 8-8s8 3.582 8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="6.5" cy="9.5" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="17.5" cy="9.5" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M9.5 20c0-2.485 1.119-4.5 2.5-4.5s2.5 2.015 2.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 18c0-1.657 1.119-3 2.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M21 18c0-1.657-1.119-3-2.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 22V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 17c0 0-5-2-5-7 3.5 0 5 3 5 7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 13c0 0 5-2 5-7-3.5 0-5 3-5 7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 12c-1.5-2-3.5-3.5-5.5-3.5C3.739 8.5 1.5 10.739 1.5 13.5S3.739 18.5 6.5 18.5c2 0 4-1.5 5.5-3.5 1.5 2 3.5 3.5 5.5 3.5C20.261 18.5 22.5 16.261 22.5 13.5S20.261 8.5 17.5 8.5c-2 0-4 1.5-5.5 3.5z" stroke="currentColor" strokeWidth="1.5"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2l2.5 5.5 6 .5-4.5 4 1.5 6-5.5-3-5.5 3 1.5-6L3.5 8l6-.5L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>,
];

const therapies = [
  { title: 'درمان فردی', desc: 'رشد شخصی و شفای‌بخشی متناسب با نیازهای شما.' },
  { title: 'درمان زناشویی', desc: 'تقویت رابطه و بازسازی حمیمیت برای زوج‌ها.' },
  { title: 'درمان خانوادگی', desc: 'بهبود ارتباط و حل تعارض‌های خانوادگی.' },
  { title: 'درمان نوجوانان', desc: 'پشتیبانی برای نوجوانان مواجه با چالش‌های عاطفی و اجتماعی.' },
  { title: 'درمان شناختی رفتاری (CBT)', desc: 'مدیریت اضطراب، افسردگی و موارد مرتبط.' },
  { title: 'درمان تمرکز بر احساس', desc: 'شفای عاطفی و رشد از طریق تمرکز بر احساسات.' },
];

const issues = [
  'اضطراب', 'افسردگی', 'مشکلات رابطه‌ای', 'غم و سوگ', 'احترام به خود',
  'کمک به والدین', 'چالش‌های شغلی', 'خیانت', 'مشکلات خانوادگی', 'چالش‌های نوجوانی',
];

const testimonials = [
  { text: 'میترا کمک کرد تا آرام شوم، اضطراب خود را درک کنم و کنترل داشته باشم.', author: 'الف، تورنتو' },
  { text: 'سرانجام احساس می‌کنم درک شده‌ام. جلسات ما ملایم اما عملی است.', author: 'س، نورث یورک' },
  { text: 'ما بهتر ارتباط برقرار می‌کنیم و کمتر دعوا می‌کنیم. درمان واقعاً تغییر داد.', author: 'ج و ر، زوج' },
];

const faqs = [
  { q: 'جلسه اول چگونه است؟', a: 'ما درباره آنچه شما را آورده است، آنچه می‌خواهید تغییر دهید و آنچه کار کرده یا نکرده است صحبت می‌کنیم. شما سرعت را تعیین می‌کنید—بدون فشار و قضاوت.' },
  { q: 'چند جلسه نیاز دارم؟', a: 'این به شخص و اهداف شما بستگی دارد. برخی از ۶-۸ جلسه کوتاه‌مدت سود می‌برند، دیگران حمایت مستمری ترجیح می‌دهند.' },
  { q: 'آیا مشاوره رایگان ارائه می‌دهید؟', a: 'بله. مشاوره رایگان ۱۵ دقیقه‌ای تلفنی یا ویدیویی به ما کمک می‌کند تا ببینیم آیا ما انتخاب خوبی برای شما هستیم.' },
  { q: 'آیا بیمه پوشش می‌دهد؟', a: 'بسیاری از پلان‌های بهداشت اضافی روان‌درمانگران ثبت‌شده را پوشش می‌دهند. لطفاً با ارائه‌دهنده خود تماس بگیرید.' },
  { q: 'آیا جلسات حضوری یا ویدیویی هستند؟', a: 'فعلاً تمام خدمات ویدیویی امن هستند. در صورت درخواست، جلسات حضوری محدود موجود است.' },
];

const LandingPageFa = () => {
  useEffect(() => {
    document.documentElement.lang = 'fa';
    document.documentElement.dir = 'rtl';
    document.title = 'روان‌درمانگر تورنتو و نورث یورک | میترا بهبودی';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'روان‌درمانی حرفه‌ای در تورنتو و نورث یورک. میترا بهبودی درمان اضطراب، درمان زناشویی و افسردگی ارائه می‌دهد. امروز برای مشاوره رایگان ثبت کنید.');
    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', `${SITE_URL}/fa`);
    return () => {
      document.documentElement.lang = 'en';
      document.documentElement.dir = 'ltr';
    };
  }, []);

  const bookNow = (e) => {
    if (window.Calendly) {
      e.preventDefault();
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    }
  };

  return (
    <div style={{ background: 'var(--color-bg-page)', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <Header />

      {/* Hero */}
      <FadeInSection>
        <section
          className={styles.hero}
          style={{ backgroundImage: 'url(/hero.jpg)', backgroundPosition: 'center', backgroundSize: 'cover', backgroundRepeat: 'no-repeat' }}
        >
          <div className={styles.heroDecorations} aria-hidden="true">
            <div className={`${styles.heroDot} ${styles.heroDot1}`} />
            <div className={`${styles.heroDot} ${styles.heroDot2}`} />
            <div className={`${styles.heroDot} ${styles.heroDot3}`} />
            <div className={`${styles.heroDot} ${styles.heroDot4}`} />
          </div>
          <div className={styles.overlay}>
            <h1 className={styles.heroTitle}>روان‌درمانی و هنر درمانی برای اضطراب، تروما و گذارهای زندگی</h1>
            <p className={styles.heroSubtitle}>در پرتو آگاهی و مهربانی، خود آغاز به شفا می‌کند</p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              onClick={bookNow}
              className={`btn btn-primary btn-lg ${styles.heroBtn}`}
            >
              مشاوره رایگان را رزرو کنید
            </a>
          </div>
        </section>
      </FadeInSection>

      {/* About */}
      <FadeInSection>
        <SectionHeading id="about">درباره من</SectionHeading>
        <section className={`container ${styles.section}`}>
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <p>درمان باید ایمن، انسانی و واقعاً مفید باشد. من روش‌های مبتنی بر شواهد را با سبکی صادقانه ترکیب می‌کنم و کمک می‌کنم تا الگوها را درک کنید، احساسات سخت را مدیریت کنید و تغییرات ملایم و پایدار ایجاد کنید.</p>
              <p>رویکرد من مشارکتی، دلسوزانه و مراجع‌محور است. تلاش می‌کنم فضایی امن، محترمانه و غیرقضاوتی ایجاد کنم که در آن بتوانید تجربیات خود را کاوش کرده، احساسات را پردازش کنید و به سمت رشد معنادار حرکت کنید.</p>
              <p>خدمات درمانی را به زبان فارسی و انگلیسی ارائه می‌دهم و متعهد به مراقبت فرهنگی هستم که داستان و تجربه زیسته هر فرد را ارج می‌نهد.</p>
            </div>
            <div className="col-md-6 text-center">
              <FallbackImage src="/IMG3063.png" alt="کوه" className={styles.aboutImage} />
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Services */}
      <FadeInSection>
        <SectionHeading id="services">خدمات</SectionHeading>
        <section className={`container ${styles.section}`}>
          <div className={styles.servicesGrid}>
            {therapies.map((t, i) => (
              <FadeInCard key={i} delay={i * 80}>
                <div className={styles.serviceCard} style={{ backgroundImage: 'url(/tilebg.JPG)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className={styles.serviceIconWrap} aria-hidden="true">
                    {serviceIcons[i]}
                  </div>
                  <h5 style={{ color: 'var(--color-primary-strong)', fontWeight: 700, marginBottom: '0.45rem', fontSize: '1rem' }}>{t.title}</h5>
                  <p style={{ color: '#5a5a6a', margin: 0, fontSize: '0.92rem', lineHeight: 1.6 }}>{t.desc}</p>
                </div>
              </FadeInCard>
            ))}
          </div>
        </section>
      </FadeInSection>

      {/* Issues */}
      <FadeInSection>
        <SectionHeading id="issues">مشکلاتی که کمک می‌کنیم</SectionHeading>
        <section className={`container ${styles.section}`}>
          <div className={styles.issuesGrid}>
            {issues.map((issue, i) => (
              <FadeInCard key={i} delay={i * 55}>
                <div className={styles.issueTile}>{issue}</div>
              </FadeInCard>
            ))}
          </div>
        </section>
      </FadeInSection>

      {/* Testimonials */}
      <FadeInSection>
        <SectionHeading id="testimonials">نظرات</SectionHeading>
        <section className={`container ${styles.section}`}>
          <div className={styles.testimonialsGrid}>
            {testimonials.map((t, i) => (
              <FadeInCard key={i} delay={i * 140}>
                <div className={styles.testimonialCard} style={{ backgroundImage: 'url(/tilebg.JPG)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <blockquote className="blockquote mb-0">
                    <p style={{ color: 'var(--color-text-main)', position: 'relative', zIndex: 1, lineHeight: 1.75 }}>«{t.text}»</p>
                    <footer className="blockquote-footer" style={{ position: 'relative', zIndex: 1 }}>{t.author}</footer>
                  </blockquote>
                </div>
              </FadeInCard>
            ))}
          </div>
        </section>
      </FadeInSection>

      {/* FAQ */}
      <FadeInSection>
        <SectionHeading id="faq">سوالات متداول</SectionHeading>
        <section className={`container ${styles.section}`}>
          <div className={`accordion ${styles.faqAccordion}`} id="faqAccordionFa">
            {faqs.map((faq, i) => (
              <div className="accordion-item" key={i}>
                <h2 className="accordion-header" id={`headingFa${i}`}>
                  <button
                    className={`accordion-button${i !== 0 ? ' collapsed' : ''}`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapseFa${i}`}
                    aria-expanded={i === 0 ? 'true' : 'false'}
                    aria-controls={`collapseFa${i}`}
                  >
                    {faq.q}
                  </button>
                </h2>
                <div
                  id={`collapseFa${i}`}
                  className={`accordion-collapse collapse${i === 0 ? ' show' : ''}`}
                  aria-labelledby={`headingFa${i}`}
                  data-bs-parent="#faqAccordionFa"
                >
                  <div className="accordion-body">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </FadeInSection>

      {/* Contact */}
      <FadeInSection>
        <SectionHeading id="contact">تماس</SectionHeading>
        <section className={`container ${styles.contactSection}`}>
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <h2 style={{ color: 'var(--color-primary-strong)', fontWeight: 700, marginBottom: '0.9rem' }}>آماده‌اید سفر خود را شروع کنید؟</h2>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem', lineHeight: 1.7 }}>مشاوره رایگان را رزرو کنید یا با سوالات خود تماس بگیرید. من اینجا هستم تا به شما کمک کنم تعادل و قدرت پیدا کنید.</p>
              <ul className="list-unstyled" style={{ marginBottom: '1.6rem' }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>ایمیل:</strong> <a href="mailto:mitratherapy@gmail.com" style={{ color: 'var(--color-accent)' }}>mitratherapy@gmail.com</a></li>
                <li><strong>تلفن:</strong> <a href="tel:+16478647160" style={{ color: 'var(--color-accent)' }}><span dir="ltr">+1 647-864-7160</span></a></li>
              </ul>
              <a href={CALENDLY_URL} target="_blank" rel="noreferrer" onClick={bookNow} className="btn btn-primary">
                هم‌اکنون رزرو کنید
              </a>
            </div>
            <div className="col-md-6 text-center">
              <FallbackImage src="/mb.png" alt="تماس" style={{ maxWidth: 180 }} />
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Footer */}
      <footer className={styles.footer}>
        <FallbackImage src="/mb.png" alt="لوگو" style={{ width: 46, marginBottom: 10 }} />
        <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.2rem' }}>روان‌درمانی میترا بهبودی</div>
        <div style={{ fontSize: '0.82rem', color: 'rgba(229, 231, 235, 0.65)', marginBottom: '0.25rem' }}>© ۲۰۲۴ تمام حقوق محفوظ است.</div>
        <div className={styles.socialLinks}>
          <a className={styles.socialLink} href="https://www.linkedin.com/in/mitrabehboudi/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.62 0 4.29 2.38 4.29 5.48v6.26zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45z"/>
            </svg>
          </a>
          <a className={styles.socialLink} href="https://www.instagram.com/mitra_bhb?igsh=bnl3eHFzanozd2Jh" target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm0 2h10c1.66 0 3 1.34 3 3v10c0 1.66-1.34 3-3 3H7c-1.66 0-3-1.34-3-3V7c0-1.66 1.34-3 3-3zm11.25 1.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5zM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
            </svg>
          </a>
        </div>
        <div style={{ fontSize: '0.76rem', color: 'rgba(229, 231, 235, 0.45)' }}>سیاست حریم خصوصی | شرایط استفاده</div>
      </footer>
    </div>
  );
};

export default LandingPageFa;
